import React from "react";
import { cn } from "@/lib/utils";
import { useAppI18n } from "@/hooks/useAppI18n";

export interface BatchFormState {
  batchName: string;
  aboutBatch: string;
  startDate: string;
  endDate: string;
  enrolmentEndDate: string;
  issueCertificate: boolean;
  enableDiscussion: boolean;
  batchType: string;
  selectedMentorIds: string[];
  acceptTerms: boolean;
}

interface BatchFormFieldsProps {
  form: BatchFormState;
  handleField: <K extends keyof BatchFormState>(key: K, value: BatchFormState[K]) => void;
  setForm: React.Dispatch<React.SetStateAction<BatchFormState>>;
  labelClass?: string;
  inputClass?: string;
  disabledFields?: (keyof BatchFormState)[];
}

export function BatchFormFields({
  form,
  handleField,
  setForm,
  labelClass = "block text-sm font-medium text-sunbird-obsidian mb-1 font-rubik",
  inputClass = "w-full rounded-lg border border-border px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-sunbird-theme-accent/40 focus:border-sunbird-theme-accent bg-white font-rubik",
  disabledFields = [],
}: BatchFormFieldsProps) {
  const { t } = useAppI18n();
  const today = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  })();
  return (
    <>
      {/* 1. Name of Batch */}
      <div>
        <label htmlFor="batchName" className={labelClass}>
          {t("batch.nameOfBatch")} <span className="text-red-500">*</span>
        </label>
        <input
          id="batchName"
          type="text"
          className={inputClass}
          placeholder={t("batch.enterBatchName")}
          value={form.batchName}
          onChange={(e) => handleField("batchName", e.target.value)}
          disabled={disabledFields.includes("batchName")}
          required
        />
      </div>

      {/* 2. About Batch */}
      <div>
        <label htmlFor="aboutBatch" className={labelClass}>
          {t("batch.aboutBatch")}
        </label>
        <textarea
          id="aboutBatch"
          rows={3}
          className={cn(inputClass, "resize-none")}
          placeholder={t("batch.briefDescBatch")}
          value={form.aboutBatch}
          onChange={(e) => handleField("aboutBatch", e.target.value)}
          disabled={disabledFields.includes("aboutBatch")}
        />
      </div>

      {/* 3-5. Dates */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {/* Start Date (required) */}
        <div>
          <label htmlFor="startDate" className={labelClass}>
            {t("batch.startDate")} <span className="text-red-500">*</span>
          </label>
          <input
            id="startDate"
            type="date"
            className={inputClass}
            value={form.startDate}
            min={today}
            onChange={(e) => {
              const newStart = e.target.value;
              const updates: Partial<BatchFormState> = { startDate: newStart };
              if (form.enrolmentEndDate && newStart > form.enrolmentEndDate)
                updates.enrolmentEndDate = "";
              if (form.endDate && newStart > form.endDate)
                updates.endDate = "";
              setForm((prev) => ({ ...prev, ...updates }));
            }}
            disabled={disabledFields.includes("startDate")}
            required
          />
        </div>

        {/* End Date — optional; must be >= startDate AND >= enrolmentEndDate */}
        <div>
          <label htmlFor="endDate" className={labelClass}>
            {t("batch.endDate")}
          </label>
          <input
            id="endDate"
            type="date"
            className={inputClass}
            value={form.endDate}
            min={
              form.enrolmentEndDate && form.enrolmentEndDate > (form.startDate || "")
                ? form.enrolmentEndDate
                : form.startDate || today
            }
            onChange={(e) => handleField("endDate", e.target.value)}
            disabled={disabledFields.includes("endDate")}
          />
          {form.enrolmentEndDate && !form.endDate && (
             <p className="mt-0.5 text-xs text-amber-600 font-rubik">
              {t("batch.mustBeAfterEnrolmentEnd")}
            </p>
          )}
        </div>

        {/* Enrolment End Date — optional; bounded between startDate and endDate */}
        <div>
          <label htmlFor="enrolmentEndDate" className={labelClass}>
            {t("batch.enrolmentEndDate")}
          </label>
          <input
            id="enrolmentEndDate"
            type="date"
            className={inputClass}
            value={form.enrolmentEndDate}
            min={form.startDate || today}
            max={form.endDate || undefined}
            onChange={(e) => handleField("enrolmentEndDate", e.target.value)}
            disabled={disabledFields.includes("enrolmentEndDate")}
          />
          {form.startDate && (
            <p className="mt-0.5 text-xs text-muted-foreground font-rubik">
              {form.endDate ? t("batch.betweenStartAndEndDate") : t("batch.betweenStartDate")}
            </p>
          )}
        </div>
      </div>
    </>
  );
}
